import type { PathAnalyticsQueryVariables } from '~/api/graphql/queries/pathAnalytic.generated';
import type { IBasePathAnalyticPageWithParent } from '../types';
import type { EAnalyticMode } from '~/modules/analytics/types';
import type { AnalyticDateFilter } from '~/presentation/ui';

import { useCustomPathAnalyticsQuery, type CustomPathAnalyticsResponse } from '../api';
import { usePathAnalysisMetricParameter } from './usePathAnalysisMetricParameter';
import { usePricingPlanActive, useLimitPathAnalysis } from '~/modules/pricing';
import { useBaseCachedQuery } from '~/modules/apps/websocket';
import { usePathAnalysisStore } from '../stores';
import { useMemo } from 'react';

export type PathAnalysisResult = ReturnType<typeof useFetchPathAnalysis>;
export interface IFetchPathAnalysisProps {
  currentDates: AnalyticDateFilter;
  previousDates: AnalyticDateFilter;
  mode?: EAnalyticMode;
  campaignVariantID?: string;
  campaignVersionID?: string;
  enabledQuery?: boolean;
}

export const useFetchPathAnalysis = ({ ...props }: IFetchPathAnalysisProps) => {
  const cacheData = usePathAnalysisStore((state) => state.cacheData);
  const upsertCacheData = usePathAnalysisStore((state) => state.upsertCacheData);
  const setDataKeyErrors = usePathAnalysisStore((state) => state.setDataKeyErrors);
  const checkAndDeleteDataKeyErrors = usePathAnalysisStore(
    (state) => state.checkAndDeleteDataKeyErrors,
  );

  const { activePlanKey } = usePricingPlanActive();
  const { isEnabled } = useLimitPathAnalysis({ planKey: activePlanKey });
  const {
    parameterKey,
    parameter,
    isEnabledQuery,
    previousParameterKey,
    previousParameter,
    locationPath,
  } = usePathAnalysisMetricParameter(props);

  // isConnected is handled internally by useBaseCachedQuery
  const isEnabledQueryFetch = isEnabledQuery && !!props.enabledQuery && isEnabled && !!locationPath;

  const {
    data: dataMetric,
    isLoading,
    refetch,
  } = useBaseCachedQuery<
    CustomPathAnalyticsResponse['pathAnalytics'],
    CustomPathAnalyticsResponse,
    PathAnalyticsQueryVariables
  >({
    cache: cacheData,
    upsertCache: upsertCacheData,
    parameterKey,
    enabled: isEnabledQueryFetch,
    getResponseData: (r) => r.pathAnalytics,
    useQuery: useCustomPathAnalyticsQuery,
    variables: parameter,
    onSuccess: () => checkAndDeleteDataKeyErrors(useCustomPathAnalyticsQuery.getKey(parameter)),
    onError: () => setDataKeyErrors(useCustomPathAnalyticsQuery.getKey(parameter)),
    onSettled: (response, error) => {
      if (error) {
        upsertCacheData(parameterKey, { requestId: response?.headers['x-request-id'] ?? '' });
      }
    },
  });

  const { data: dataPreviousMetric, isLoading: isLoadingPreviousMetric } = useBaseCachedQuery<
    CustomPathAnalyticsResponse['pathAnalytics'],
    CustomPathAnalyticsResponse,
    PathAnalyticsQueryVariables
  >({
    cache: cacheData,
    upsertCache: upsertCacheData,
    parameterKey: previousParameterKey,
    enabled: isEnabledQueryFetch,
    getResponseData: (r) => r.pathAnalytics,
    useQuery: useCustomPathAnalyticsQuery,
    variables: previousParameter,
  });

  // Guard: when query is disabled, treat as not loading
  const isLoadingMetric = isLoading && isEnabledQueryFetch;

  const previousPathAnalytics = useMemo<IBasePathAnalyticPageWithParent[]>(() => {
    const pageGroups = dataPreviousMetric?.flatMap((g) => g?.pathGroups?.flatMap((g) => g?.paths));
    return (
      pageGroups?.filter((page): page is IBasePathAnalyticPageWithParent => page !== undefined) ||
      []
    );
  }, [dataPreviousMetric]);

  const previousPathAnalyticsMap = useMemo(() => {
    return previousPathAnalytics.reduce(
      (acc, item) => {
        acc[item.id] = item;
        return acc;
      },
      {} as Record<string, IBasePathAnalyticPageWithParent>,
    );
  }, [previousPathAnalytics]);

  return {
    data: dataMetric ?? [],
    isLoading: isLoadingMetric,
    isLoadingPrevious: isLoadingPreviousMetric,
    previousPathAnalyticsMap,
    refetch,
  };
};
